/**
 * SAAS-USD-BILLING-1 · Phase 0.5 — Overview banner nudging UNCLASSIFIED
 * merchants to pick a Business Type. Non-destructive: dashboard remains
 * fully usable if the merchant ignores it.
 *
 * Fetches its own `/merchant/store` so it can be dropped into any page
 * without touching the parent's data-fetch code. When the merchant picks
 * a type it optimistically hides itself.
 */
import { useEffect, useState } from "react";
import { AlertCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import BusinessTypeSelector from "./BusinessTypeSelector";

export default function BusinessTypeBanner({ variant = "banner" }) {
  const [store, setStore] = useState(null);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/merchant/store")
      .then((r) => setStore(r.data))
      .catch(() => setStore(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !store) return null;
  const type = store.merchant_type || "UNCLASSIFIED";
  if (type !== "UNCLASSIFIED") return null;

  return (
    <>
      <div
        className={`pf-card mb-6 overflow-hidden relative border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 ${
          variant === "compact" ? "" : ""
        }`}
        data-testid="business-type-banner"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-cyan-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="relative p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-4 items-center">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
              <AlertCircle className="w-4 h-4" /> Tell us about your business
            </div>
            <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-slate-900 mt-2">
              Set your Business Type
            </h3>
            <p className="text-xs md:text-sm text-slate-600 mt-2 max-w-xl leading-relaxed">
              Select your business type so Cyannect can configure the correct campaign and billing
              options for your business.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="pf-btn-primary px-4 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="business-type-banner-cta"
          >
            Choose Business Type <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <BusinessTypeSelector
        open={open}
        onClose={() => setOpen(false)}
        currentValue={type}
        locked={!!store.merchant_type_locked}
        onSaved={(next) =>
          setStore((s) => (s ? { ...s, merchant_type: next } : s))
        }
      />
    </>
  );
}
